import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useRef,
  type ReactNode,
} from "react";
import { usePostHog } from "posthog-react-native";

import { useApiClient } from "../../api/louise-api";
import type { CoachDisclosure } from "../disclosure";

/**
 * The receipt of what she agreed to, and the record of the walk it authorised.
 *
 * Until this file the coach kept nothing. She read eight claims, tapped
 * "continue", the sheet closed and the object was discarded — so "what did she
 * agree to, and when" had no answer anywhere. GDPR Article 7(1) requires being
 * able to *demonstrate* consent and Article 9 makes this health data, which is
 * what `dashboard-api` #1834 exists to hold.
 *
 * ### Nothing here can carry what the camera saw
 *
 * This is the one module in the package allowed to write to the Louise API, and
 * `no-retention.test.ts` names it explicitly for that. The permission is narrow
 * on purpose: every other forbidden capability still applies here — no
 * recorder, no canvas, no file system, no peer connection — so what this file
 * can send is what it is holding, which is a version string, a locale, the
 * disclosure text she was shown, and the shape of a walk. There is no path from
 * a frame to this code.
 *
 * ### Best effort, always, because the needle does not wait
 *
 * Her rule, and it decides the whole design: *if the network drops during the
 * procedure it has to keep working.* So nothing here is awaited by a screen,
 * nothing gates a button, and no failure reaches her. A receipt that cannot be
 * written is a receipt we do not have — it is never a reason to interrupt a
 * woman holding a needle.
 *
 * Failures are therefore silent **to her** and loud **to us**: each one is
 * captured as an analytics event with the stage that failed, so a systematic
 * outage is visible in a dashboard rather than inferred from missing rows. The
 * alternative — a toast, a retry button — would put an infrastructure problem
 * on the screen at the exact moment #2267 says nothing must compete with the
 * procedure.
 *
 * ### What the ids are for
 *
 * The consent id is what the session hangs from — the database refuses a
 * session without it — and the session id is what events and the completion
 * hang from. Both are held in refs rather than state: they must be readable by
 * the callback that fires next, and a re-render is not what they are for. If
 * the consent write failed, the session write is skipped rather than sent
 * unauthorised, and the events with it. Partial records are worse than none:
 * they read as a walk nobody consented to.
 */

/** What a walk is, in the shape the API records it. */
export type CoachWalkRecord = {
  protocolSlug: string;
  protocolVersion: string;
  brandName: string;
  deviceForm: string;
  cameraEnabled: boolean;
  /** Her scheduled dose, when the walk was opened against one. */
  patientMedicationId?: string;
};

/**
 * The events the walk reports.
 *
 * A subset of what the API accepts, listed here rather than imported wholesale
 * so a new event type is a decision rather than an autocomplete.
 */
export type CoachEvent =
  | "session_started"
  | "medication_confirmed"
  | "step_presented"
  | "patient_ready"
  | "paused"
  | "escalated"
  | "completed"
  | "abandoned";

/** How a walk ended, in the API's terms. */
export type CoachOutcome =
  | "outcome_unknown"
  | "coach_session_completed"
  | "patient_confirmed_medication_taken";

/**
 * One recorder for the whole coach, and why it has to be exactly one.
 *
 * The consent id lives in a ref, and a hook called in two components is two
 * instances with two refs. The disclosure sheet wrote the receipt into its own
 * and the walk looked for it in another, found nothing, and skipped the
 * session — silently, because "no receipt, no session" is the rule that keeps
 * an unauthorised walk from being recorded. The rule was right; the plumbing
 * was not. Held on a context mounted beside the coach itself, so the sheet and
 * every walk under it share the same ids.
 */
/** Six steps and a readiness is the whole walk; past this the session failed. */
const MAX_PENDING_EVENTS = 10;

const CoachRecordsContext = createContext<CoachRecords | null>(null);

export type CoachRecords = ReturnType<typeof useCoachRecordsValue>;

export function CoachRecordsProvider({ children }: { children: ReactNode }) {
  const records = useCoachRecordsValue();
  return createElement(
    CoachRecordsContext.Provider,
    { value: records },
    children,
  );
}

/**
 * The recorder, or a set of no-ops where nothing provides one.
 *
 * Silence rather than a crash: this is mounted at the root beside the coach,
 * and a surface that somehow renders a walk outside it should still guide her
 * through an injection. What it must not do is invent a second recorder, which
 * is the bug this replaced.
 */
export function useCoachRecords(): CoachRecords {
  return useContext(CoachRecordsContext) ?? NO_RECORDS;
}

const NO_RECORDS: CoachRecords = {
  recordConsent: async () => {},
  startWalk: async () => {},
  recordEvent: async () => {},
  finishWalk: async () => {},
  abandonWalk: async () => {},
  forgetWalk: () => {},
  forgetConsent: () => {},
};

function useCoachRecordsValue() {
  const { client, patientId } = useApiClient();
  const posthog = usePostHog();

  const consentIdRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  /**
   * Events raised before the session exists.
   *
   * The walk reports its first step in the same commit that starts the
   * session, and the session id only arrives a round trip later — so the first
   * `step_presented`, the one naming the step she is actually looking at, was
   * dropped every single time. Held until there is somewhere to put them, then
   * sent in order, with their original timestamps: what is recorded is when it
   * happened, not when the network caught up.
   *
   * Bounded, because an unbounded buffer on a failing network is a leak. Six
   * steps and a readiness is the whole walk, so ten is generous; past that the
   * session write has plainly failed and the events have nowhere to go anyway.
   */
  const pendingRef = useRef<
    { eventType: CoachEvent; stepId?: string; occurredAt: string }[]
  >([]);

  /**
   * One place where a failure stops.
   *
   * Every call below goes through it, so there is exactly one answer to "what
   * happens when this fails" and it is the same answer everywhere.
   */
  const attempt = useCallback(
    async (stage: string, write: () => Promise<{ error?: unknown }>) => {
      try {
        const response = await write();
        if (response.error) throw response.error;
        return true;
      } catch {
        posthog?.capture("injection_coach_record_failed", { stage });
        return false;
      }
    },
    [posthog],
  );

  const recordConsent = useCallback(
    async (disclosure: CoachDisclosure, locale: "en" | "fr", text: string) => {
      if (!patientId) return;
      // Cleared before the write, not after it: a walk started while this is
      // in flight must find nothing and record nothing, rather than attach
      // itself to the previous receipt.
      consentIdRef.current = null;
      await attempt("consent", async () => {
        const response = await client.POST(
          "/patients/{patientId}/injection-coach/consents",
          {
            params: { path: { patientId } },
            body: {
              version: disclosure.version,
              locale,
              text,
              // None, and the current disclosure refuses all four. The field is
              // sent rather than omitted so the receipt records that nothing
              // was granted, which is a different fact from not being asked.
              grants: [],
            },
          },
        );
        if (!response.error) {
          consentIdRef.current = response.data?.data?.id ?? null;
        }
        return response;
      });
    },
    [attempt, client, patientId],
  );

  const sendEvent = useCallback(
    (
      sessionId: string,
      event: { eventType: CoachEvent; stepId?: string; occurredAt: string },
    ) =>
      attempt(`event:${event.eventType}`, () =>
        client.POST(
          "/patients/{patientId}/injection-coach/sessions/{sessionId}/events",
          {
            params: { path: { patientId: patientId!, sessionId } },
            body: {
              eventType: event.eventType,
              occurredAt: event.occurredAt,
              ...(event.stepId ? { stepId: event.stepId } : {}),
            },
          },
        ),
      ),
    [attempt, client, patientId],
  );

  const startWalk = useCallback(
    async (walk: CoachWalkRecord) => {
      // No receipt, no session. A session the database would refuse anyway, and
      // a row saying a walk ran under a consent we cannot produce is worse than
      // the absence of both.
      if (!patientId || !consentIdRef.current) return;
      await attempt("session", async () => {
        const response = await client.POST(
          "/patients/{patientId}/injection-coach/sessions",
          { params: { path: { patientId } }, body: walk },
        );
        if (!response.error) {
          sessionIdRef.current = response.data?.data?.id ?? null;
        }
        return response;
      });

      // Whatever the walk reported while the session was opening, in the order
      // it happened. If the session never opened there is nowhere to put it,
      // and the queue is dropped rather than carried into the next walk.
      const queued = pendingRef.current;
      pendingRef.current = [];
      const sessionId = sessionIdRef.current;
      if (!sessionId) return;
      for (const event of queued) await sendEvent(sessionId, event);
    },
    [attempt, client, patientId, sendEvent],
  );

  const recordEvent = useCallback(
    async (eventType: CoachEvent, stepId?: string) => {
      if (!patientId) return;
      // Stamped here rather than on arrival: the record should say when she
      // reached the step, not when the request left.
      const event = {
        eventType,
        ...(stepId ? { stepId } : {}),
        occurredAt: new Date().toISOString(),
      };

      const sessionId = sessionIdRef.current;
      if (!sessionId) {
        // Either the session is still being opened — which is the usual case
        // for the first step — or there is none and never will be. Held either
        // way; `startWalk` flushes what it can and drops the rest.
        if (pendingRef.current.length < MAX_PENDING_EVENTS) {
          pendingRef.current.push(event);
        }
        return;
      }
      await sendEvent(sessionId, event);
    },
    [patientId, sendEvent],
  );

  const finishWalk = useCallback(
    async (outcome: CoachOutcome) => {
      const sessionId = sessionIdRef.current;
      if (!patientId || !sessionId) return;
      /**
       * Closed here, before the request, and not after it.
       *
       * The id is what "there is an open session" means, so releasing it
       * synchronously makes every later caller a no-op — which is exactly what
       * they should be. Two of them are reachable in the same second: she
       * finishes, the completion is still in flight, she taps the button that
       * closes the coach, and the unmount would have recorded the walk as
       * abandoned and closed it a second time with no outcome, overwriting the
       * one that says she completed it. Releasing after the response also
       * risked clearing a *newer* walk's id, since "Start over" is on the same
       * card.
       *
       * The cost is that a completion which fails is not retried. It is the
       * right cost: the alternative is a record that contradicts itself, and a
       * failure is already reported.
       */
      sessionIdRef.current = null;
      pendingRef.current = [];
      await attempt("completion", () =>
        client.POST(
          "/patients/{patientId}/injection-coach/sessions/{sessionId}/completion",
          { params: { path: { patientId, sessionId } }, body: { outcome } },
        ),
      );
    },
    [attempt, client, patientId],
  );

  /** A new walk starts from nothing: no stale session collects its events. */
  const forgetWalk = useCallback(() => {
    sessionIdRef.current = null;
    pendingRef.current = [];
  }, []);

  /**
   * And a new *consent* starts from nothing either.
   *
   * The receipt is what authorises the walk, so it may not outlive the sheet
   * that produced it. Without this, a walk opened later — on a surface that
   * shows no disclosure of its own — would attach itself to whatever consent
   * happened to be in hand, and the record would say she agreed to something
   * she was never shown for that walk. Cleared when the coach opens and when
   * it closes, so the only walks that are recorded are the ones a disclosure
   * was read for.
   */
  const forgetConsent = useCallback(() => {
    consentIdRef.current = null;
    sessionIdRef.current = null;
    pendingRef.current = [];
  }, []);

  /**
   * The walk went away without ending, if one was open.
   *
   * Separate from `recordEvent("abandoned")` because of what the queue is for.
   * An event raised before the session exists is held, so the first step is not
   * lost to the round trip that opens it — but an abandonment raised *after* a
   * session closed belongs to no walk at all, and holding it meant the next
   * walk opened with someone else's ending in its log. A chained pair makes
   * that deterministic: the first half finishes, its session closes, the
   * component unmounts to ask the second half's question, and the queue carried
   * `abandoned` into the second injection's record.
   *
   * So this asks first. No session, nothing to abandon, nothing queued.
   */
  const abandonWalk = useCallback(async () => {
    if (!sessionIdRef.current) return;
    await recordEvent("abandoned");
    await finishWalk("outcome_unknown");
  }, [recordEvent, finishWalk]);

  return {
    recordConsent,
    startWalk,
    recordEvent,
    finishWalk,
    abandonWalk,
    forgetWalk,
    forgetConsent,
  };
}
